import React, { useEffect, useState } from "react";
import Card from "../../../components/ui/card-snippet";
import { useQueryParams } from "../../../contexts/queryParamsContext";
import {
  PromoCodeDiscountTypeEnum,
  respEnum,
  RoleEnum,
} from "../../../constants/appConstant";
import Common from "../../../helper/common";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";

export default function PromoCodesDetailView() {
  const [promoDetails, setPromoDetails] = useState(null);
  const { id, setId } = useQueryParams();
  const [roleId, setRoleId] = useState(null);

  useEffect(() => {
    (async () => {
      setRoleId(global.user.roleId);
    })();
  }, []);

  useEffect(() => {
    if (id) {
      fetchPromoCodeDetail();
    }

    return () => {
      setId(null);
    };
  }, [id]);

  const fetchPromoCodeDetail = async () => {
    const response = await Common.ApiService.getInstance().request(
      `GetPromoCodeDetail?id=${id}`
    );

    if (response.status === respEnum.Success && response?.data?.length > 0) {
      setPromoDetails(response.data[0]);
    }
  };

  if (!promoDetails) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading promo code details...</p>
        </div>
      </div>
    );
  }

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <div className="space-y-6 mb-5">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">🎫 {promoDetails.code}</h1>
            <p className="text-blue-100">Promo Code Details & Restrictions</p>
          </div>
          <div className="text-right">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                promoDetails.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full mr-2 ${
                  promoDetails.isActive ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>
              {promoDetails.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        </div>
      </div>

      {/* Basic Information Section */}
      <Card title="">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <span className="w-2 h-6 bg-blue-500 rounded-full mr-3"></span>
            Basic Information
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DetailField
              label="Promo Code"
              value={promoDetails.code}
              icon="🎫"
            />
            <DetailField
              label="Discount Type"
              value={
                promoDetails.discountType ===
                PromoCodeDiscountTypeEnum.Percentage
                  ? "Percentage"
                  : "Fixed Amount"
              }
              icon="💰"
            />
            <DetailField
              label="Discount Value"
              value={`${promoDetails.discountValue}${
                promoDetails.discountType ===
                PromoCodeDiscountTypeEnum.Percentage
                  ? "%"
                  : ""
              }`}
              icon="📊"
            />
            <DetailField
              label="Valid From"
              value={Common.Utility.formatDateTime(promoDetails.validFrom)}
              icon="📅"
            />
            <DetailField
              label="Valid To"
              value={Common.Utility.formatDateTime(promoDetails.validTo)}
              icon="📅"
            />
            <DetailField
              label="Min Order Amount"
              value={
                promoDetails.minOrderAmount
                  ? `${promoDetails.minOrderAmount}`
                  : "No minimum"
              }
              icon="🛒"
            />
            {promoDetails.discountType ===
              PromoCodeDiscountTypeEnum.Percentage && (
              <DetailField
                label="Max Discount"
                value={
                  promoDetails.maxDiscount
                    ? `${promoDetails.maxDiscount}`
                    : "No limit"
                }
                icon="🔒"
              />
            )}
            <DetailField
              label="Usage Limit"
              value={promoDetails.usageLimit || "Unlimited"}
              icon="🔢"
            />
            <DetailField
              label="User Usage Limit"
              value={promoDetails.userUsageLimit || "Unlimited"}
              icon="👤"
            />
            <DetailField
              label="Total Redeemed"
              value={promoDetails.totalRedeemed || 0}
              icon="✅"
            />
          </div>
        </div>
      </Card>

      {/* Restrictions Section */}
      <Card title="">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <span className="w-2 h-6 bg-green-500 rounded-full mr-3"></span>
            Restrictions & Conditions
          </h2>
          <div className="space-y-6">
            {/* Sport Restriction */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                <span className="w-1.5 h-4 bg-orange-400 rounded-full mr-2"></span>
                Sport Restriction
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🏆</span>
                <span className="text-lg font-medium text-gray-800">
                  {promoDetails.sportName || "All Sports"}
                </span>
              </div>
            </div>

            {/* Booking Time Window */}
            {(promoDetails.bookingFrom || promoDetails.bookingTill) && (
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="w-1.5 h-4 bg-purple-400 rounded-full mr-2"></span>
                  Booking Time Window
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {promoDetails.bookingFrom && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">🕐</span>
                      <div>
                        <p className="text-sm text-gray-600">From</p>
                        <p className="font-medium">
                          {Common.Utility.formatDateTime(
                            promoDetails.bookingFrom
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  {promoDetails.bookingTill && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">🕐</span>
                      <div>
                        <p className="text-sm text-gray-600">Till</p>
                        <p className="font-medium">
                          {Common.Utility.formatDateTime(
                            promoDetails.bookingTill
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Day Restrictions */}
            {promoDetails.promocodeDays &&
              promoDetails.promocodeDays.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                    <span className="w-1.5 h-4 bg-red-400 rounded-full mr-2"></span>
                    Valid Days
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {promoDetails.promocodeDays.map((dayId, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
                      >
                        📅 {daysOfWeek[dayId]}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {/* Time Range Restrictions */}
            {promoDetails.promocodeTimes &&
              promoDetails.promocodeTimes.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                    <span className="w-1.5 h-4 bg-teal-400 rounded-full mr-2"></span>
                    Valid Time Ranges
                  </h3>
                  <div className="space-y-2">
                    {promoDetails.promocodeTimes.map((timeRange, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 p-3 bg-teal-50 rounded-lg border border-teal-200"
                      >
                        <span className="text-xl">⏰</span>
                        <span className="font-medium">
                          {timeRange.timeFrom} - {timeRange.timeTill}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Additional Restrictions */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                <span className="w-1.5 h-4 bg-gray-400 rounded-full mr-2"></span>
                Additional Restrictions
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {roleId && roleId === RoleEnum.Super_Admin && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">🆕</span>
                      <div>
                        <p className="text-sm text-gray-600">First Time Only</p>
                        <p className="font-medium">
                          {promoDetails.isFirstTimeOnly ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">🏢</span>
                      <div>
                        <p className="text-sm text-gray-600">Club Specific</p>
                        <p className="font-medium">
                          {promoDetails.isClubSpecific ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                    {promoDetails.isClubSpecific && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">🏢</span>
                        <div>
                          <p className="text-sm text-gray-600">Club</p>
                          <p className="font-medium">
                            {promoDetails.clubName || "N/A"}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center space-x-2">
                  <span className="text-xl">👤</span>
                  <div>
                    <p className="text-sm text-gray-600">Player Specific</p>
                    <p className="font-medium">
                      {promoDetails.isPlayerSpecific ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                {promoDetails.isPlayerSpecific && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">📱</span>
                      <div>
                        <p className="text-sm text-gray-600">Customer Phone</p>
                        <p className="font-medium">{promoDetails.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">👤</span>
                      <div>
                        <p className="text-sm text-gray-600">Player Name</p>
                        <p className="font-medium">{promoDetails.playerName}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Terms and Conditions Section */}
      <Card title="">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <span className="w-2 h-6 bg-purple-500 rounded-full mr-3"></span>
            Terms & Conditions
          </h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="prose max-w-none">
              <ul className="space-y-3">
                {promoDetails.termsAndConditions
                  .split(".")
                  .filter((point) => point.trim() !== "")
                  .map((point, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 leading-relaxed">
                        {point.trim()}.
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

const DetailField = ({ label, value, icon }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className="flex items-center space-x-3">
      {icon && <span className="text-2xl">{icon}</span>}
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-500 block mb-1">
          {label}
        </span>
        <p className="text-lg font-semibold text-gray-900">{value || "-"}</p>
      </div>
    </div>
  </div>
);
